/**
 * Persistencia local (por navegador) de todo lo relacionado con monedas:
 *
 *   - displayCurrency: la moneda en la que el usuario quiere VER el dashboard
 *     ('MXN' por defecto | 'USD'). La cambia el toggle del header.
 *   - sheetCurrency:   mapa { [nombreHoja]: 'MXN'|'USD' } — en qué moneda están
 *     NATIVAMENTE los pagos de cada producto/hoja. Se configura en el modal de
 *     Configuración. Default 'MXN'.
 *   - cachedRates:     tasas base-USD cacheadas + timestamp, para no pegarle a la
 *     API en cada carga.
 *   - rateOverride:    tasa USD→MXN manual opcional. Si está definida, gana sobre
 *     la de internet.
 *
 * Espejo de la convención de `src/sheetsStore.js`: todo envuelto en try/catch
 * para tolerar navegadores con localStorage deshabilitado.
 */

export const SUPPORTED_CURRENCIES = ['MXN', 'USD'];
export const DEFAULT_CURRENCY = 'MXN';

const KEY_DISPLAY = 'dashboard.displayCurrency.v1';
const KEY_SHEETMAP = 'dashboard.sheetCurrency.v1';
const KEY_RATES = 'dashboard.fxRates.v1';
const KEY_OVERRIDE = 'dashboard.fxOverride.v1';

// ─── Moneda de display (toggle del header) ──────────────────────────────────

export function getDisplayCurrency() {
  try {
    const v = localStorage.getItem(KEY_DISPLAY);
    return SUPPORTED_CURRENCIES.includes(v) ? v : DEFAULT_CURRENCY;
  } catch (e) {
    return DEFAULT_CURRENCY;
  }
}

export function setDisplayCurrency(code) {
  try {
    if (SUPPORTED_CURRENCIES.includes(code)) localStorage.setItem(KEY_DISPLAY, code);
  } catch (e) {}
}

// ─── Moneda nativa por hoja/producto ────────────────────────────────────────

function readSheetMap() {
  try {
    const raw = localStorage.getItem(KEY_SHEETMAP);
    const obj = raw ? JSON.parse(raw) : null;
    return obj && typeof obj === 'object' ? obj : {};
  } catch (e) {
    return {};
  }
}

export function getSheetCurrency(name) {
  if (!name) return DEFAULT_CURRENCY;
  const map = readSheetMap();
  const v = map[name];
  return SUPPORTED_CURRENCIES.includes(v) ? v : DEFAULT_CURRENCY;
}

export function setSheetCurrency(name, code) {
  if (!name) return;
  try {
    const map = readSheetMap();
    if (code === DEFAULT_CURRENCY || !SUPPORTED_CURRENCIES.includes(code)) {
      delete map[name]; // default no necesita guardarse
    } else {
      map[name] = code;
    }
    localStorage.setItem(KEY_SHEETMAP, JSON.stringify(map));
  } catch (e) {}
}

// ─── Cache de tasas base-USD ─────────────────────────────────────────────────

export function getCachedRates() {
  try {
    const raw = localStorage.getItem(KEY_RATES);
    const obj = raw ? JSON.parse(raw) : null;
    if (obj && obj.rates && typeof obj.rates === 'object') return obj; // { rates, ts, source }
  } catch (e) {}
  return null;
}

export function setCachedRates(payload) {
  try {
    localStorage.setItem(KEY_RATES, JSON.stringify(payload));
  } catch (e) {}
}

// ─── Override manual de la tasa USD→MXN ──────────────────────────────────────

export function getRateOverride() {
  try {
    const raw = localStorage.getItem(KEY_OVERRIDE);
    if (raw == null || raw === '') return null;
    const n = parseFloat(raw);
    return isFinite(n) && n > 0 ? n : null;
  } catch (e) {
    return null;
  }
}

export function setRateOverride(n) {
  try {
    if (n == null || n === '' || !isFinite(parseFloat(n)) || parseFloat(n) <= 0) {
      localStorage.removeItem(KEY_OVERRIDE);
    } else {
      localStorage.setItem(KEY_OVERRIDE, String(parseFloat(n)));
    }
  } catch (e) {}
}
